import { useCallback, useEffect, useMemo } from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { useCurrency } from 'hooks/useCurrency';
import { getDesignsImageUrl } from 'helpers/imageUrl';

const analytics = AnalyticsBrowser.load({
  writeKey: process.env.REACT_APP_SEGMENT_KEY,
});

const mapItemsHelper = (items, currency) => {
  return items.map((item) => {
    return {
      image_url: getDesignsImageUrl(item.img, 450, 540),
      name: item.name,
      price: Number(item.price),
      product_id: item.id.toString(),
      quantity: item.num,
      size: item.size,
      currency,
    };
  });
};

export const useSegment = () => {
  const { pathname } = useLocation();
  const currency = useCurrency();
  const { user } = useAuth();

  const track = useCallback(
    (event, properties) => {
      analytics.track(event, properties, {
        traits: {
          email: user && user.email,
          user_name: user && user.username,
          user_id: user && user.id,
        },
      });
    },
    [user]
  );

  const productViewAction = useCallback(
    (product) => {
      track('Product Viewed', {
        product_id: product.id.toString(),
        name: product.name,
        source: 'pwa',
      });
    },
    [track]
  );

  const addToCartAction = useCallback(
    (product) => {
      track('Product Added', {
        product_id: product.productId.toString(),
        category: product.category,
        name: product.name,
        price: Number(product.price[currency.code]),
        variant: product.variant,
        quantity: Number(product.quantity),
        currency: currency.code,
        source: 'pwa',
      });
    },
    [currency.code, track]
  );

  const cartViewedAction = useCallback(
    (items) => {
      track('Cart Viewed', {
        products: mapItemsHelper(items, currency.code),
        currency: currency.code,
        source: 'pwa',
        is_checkout_v2: false,
      });
    },
    [currency.code, track]
  );

  const continueCheckoutAction = useCallback(
    (items, formValues, prices) => {
      const opts = {
        name: formValues.billing.addressee,
        email: formValues.contact.email,
        phone_number: formValues.contact.phone_number,
        full_name: formValues.billing.addressee,
        username: user?.username || '',
        user_id: user && user.id,
        vote_email_subscribed: formValues.signUpForUpdates,
        newsletter_subscribed: formValues.signUpForNewsletter,
        address: {
          city: formValues.billing.city,
          country: formValues.billing.country,
          line_1and2: `${formValues.billing.street1} ${formValues.billing.street1}`,
          state: formValues.billing.state,
          zip: formValues.billing.postal_code,
        },
      };

      analytics.identify(opts);

      track('Checkout Step Completed', {
        currency: currency.code,
        discount: prices.discount,
        shipping: prices.shipping_cost,
        total: prices.total,
        products: mapItemsHelper(items, currency.code),
        source: 'pwa',
      });
    },
    [currency.code, track, user]
  );

  const orderCompleteAction = useCallback(
    (order) => {
      track('Order Completed', {
        order_id: order.id.toString(),
        total: order.total,
        tax: Number(order.tax),
        currency_total: order.total,
        subtotal: Number(order.sub_total),
        shipping: Number(order.shipping_cost),
        coupon: order.discount_code,
        currency: currency.code,
        source: 'pwa',
        is_checkout_v2: false,
        products: order.items.map((item) => ({
          product_id: item.product.id.toString(),
          name: item.product.name,
          price: item.unit_price,
          quantity: item.quantity,
          category: item.sizeFull && item.sizeFull.split(' ')[0],
        })),
      });
    },
    [currency.code, track]
  );

  const getAnalytics = useCallback(() => {
    return analytics;
  }, []);

  const segmentActions = useMemo(
    () => ({
      cartViewedAction,
      continueCheckoutAction,
      productViewAction,
      addToCartAction,
      orderCompleteAction,
      getAnalytics,
    }),
    [
      cartViewedAction,
      continueCheckoutAction,
      productViewAction,
      addToCartAction,
      orderCompleteAction,
      getAnalytics,
    ]
  );

  useEffect(() => {
    analytics.page();
  }, [pathname]);

  return segmentActions;
};
